import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function PHPTimestamp() {
  const { t, i18n } = useTranslation(['programming', 'common']);
  const [copySuccess, setCopySuccess] = useState('');

  // Update SEO
  useEffect(() => {
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('phpTimestamp', currentLang);
    updateSEOTags(seoConfig, currentLang);
    document.documentElement.lang = currentLang;
  }, [i18n.language]);

  // Copy to clipboard
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(id);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const CodeBlock = ({ code, language = 'php', id }: { code: string; language?: string; id: string }) => (
    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto relative group">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-xs transition-colors opacity-0 group-hover:opacity-100"
      >
        {copySuccess === id ? t('common:button.copied') : t('common:button.copy')}
      </button>
      <pre className="text-green-400 text-sm font-mono whitespace-pre overflow-x-auto">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 py-4 md:py-8">
        {/* Page Header */}
        <div className="text-center mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            🐘 {t('php.mainTitleSEO')}
          </h1>
          <p className="text-lg text-gray-600">{t('php.subtitle')}</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <p className="text-gray-700 leading-relaxed">{t('php.intro')}</p>
        </div>

        {/* Get Current Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('php.getCurrentTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('php.getCurrentDesc')}</p>

          <div className="space-y-6">
            {/* time() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('php.method1')}
              </h3>
              <CodeBlock
                id="php-1"
                code={`<?php
// Get current timestamp (seconds since Unix Epoch)
$timestamp = time();
echo $timestamp;  // Output: 1735948800

// Most common and efficient method
// Returns integer timestamp in seconds`}
              />
              <p className="mt-2 text-sm text-gray-600">💡 time() is the most commonly used method for getting current timestamp</p>
            </div>

            {/* microtime() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('php.method2')}
              </h3>
              <CodeBlock
                id="php-2"
                code={`<?php
// Get current timestamp with microseconds
$microtime = microtime(true);
echo $microtime;  // Output: 1735948800.1234

// Returns float with microseconds
// Useful for precise timing measurements

// Get just microseconds
$microtime_parts = microtime();
echo $microtime_parts;  // Output: "0.12345600 1735948800"

// Convert to milliseconds
$milliseconds = round(microtime(true) * 1000);
echo $milliseconds;  // Output: 1735948800123`}
              />
            </div>

            {/* DateTime class */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('php.method3')}
              </h3>
              <CodeBlock
                id="php-3"
                code={`<?php
// Using DateTime class (object-oriented approach)
$datetime = new DateTime();
$timestamp = $datetime->getTimestamp();
echo $timestamp;  // Output: 1735948800

// Alternative - create from current time
$datetime = new DateTime('now');
$timestamp = $datetime->getTimestamp();
echo $timestamp;

// ✓ Recommended for complex date operations`}
              />
            </div>
          </div>
        </div>

        {/* Convert Timestamp to Date */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('php.toDateTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('php.toDateDesc')}</p>

          <div className="space-y-6">
            {/* date() function */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('php.dateFunction')}
              </h3>
              <CodeBlock
                id="php-4"
                code={`<?php
$timestamp = 1735948800;

// Basic formatting
echo date('Y-m-d H:i:s', $timestamp);
// Output: 2025-01-04 12:00:00

// Different formats
echo date('Y-m-d', $timestamp);           // 2025-01-04
echo date('d/m/Y', $timestamp);           // 04/01/2025
echo date('F j, Y', $timestamp);          // January 4, 2025
echo date('l, F j, Y', $timestamp);       // Saturday, January 4, 2025
echo date('g:i A', $timestamp);           // 12:00 PM
echo date('Y-m-d H:i:s', $timestamp);     // 2025-01-04 12:00:00

// ISO 8601 format
echo date('c', $timestamp);
// Output: 2025-01-04T12:00:00+08:00

// RFC 2822 format
echo date('r', $timestamp);
// Output: Sat, 04 Jan 2025 12:00:00 +0800`}
              />
            </div>

            {/* DateTime class */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('php.dateTimeClass')}
              </h3>
              <CodeBlock
                id="php-5"
                code={`<?php
$timestamp = 1735948800;

// Create DateTime from timestamp
$datetime = new DateTime();
$datetime->setTimestamp($timestamp);

// Format
echo $datetime->format('Y-m-d H:i:s');
// Output: 2025-01-04 12:00:00

// Alternative - create directly
$datetime = (new DateTime())->setTimestamp($timestamp);
echo $datetime->format('F j, Y, g:i A');
// Output: January 4, 2025, 12:00 PM

// Using DateTimeImmutable (recommended)
$datetime = (new DateTimeImmutable())->setTimestamp($timestamp);
echo $datetime->format('Y-m-d H:i:s');`}
              />
            </div>

            {/* Custom Formatting */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm mr-3">3</span>
                {t('php.formatDate')}
              </h3>
              <CodeBlock
                id="php-6"
                code={`<?php
$timestamp = 1735948800;

// Common date format characters:
// Y - 4-digit year (2025)
// y - 2-digit year (25)
// m - Month with leading zeros (01-12)
// n - Month without leading zeros (1-12)
// d - Day with leading zeros (01-31)
// j - Day without leading zeros (1-31)
// H - 24-hour format with leading zeros (00-23)
// h - 12-hour format with leading zeros (01-12)
// i - Minutes with leading zeros (00-59)
// s - Seconds with leading zeros (00-59)
// A - AM/PM uppercase
// a - am/pm lowercase

// Examples
echo date('Y/m/d H:i:s', $timestamp);     // 2025/01/04 12:00:00
echo date('d-M-Y', $timestamp);           // 04-Jan-2025
echo date('l, F jS Y', $timestamp);       // Saturday, January 4th 2025
echo date('D M j G:i:s T Y', $timestamp); // Sat Jan 4 12:00:00 CST 2025`}
              />
            </div>
          </div>
        </div>

        {/* Convert Date to Timestamp */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('php.toTimestampTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('php.toTimestampDesc')}</p>

          <div className="space-y-6">
            {/* strtotime() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">1</span>
                {t('php.strtotime')}
              </h3>
              <CodeBlock
                id="php-7"
                code={`<?php
// Parse absolute date strings
$timestamp = strtotime('2025-01-04 12:00:00');
echo $timestamp;  // Output: 1735948800

// Different formats
$ts1 = strtotime('2025-01-04');
$ts2 = strtotime('01/04/2025');
$ts3 = strtotime('January 4, 2025');
$ts4 = strtotime('4 Jan 2025 12:00:00');

echo date('Y-m-d', $ts1);  // 2025-01-04

// Relative dates (most powerful feature)
$now = time();
$tomorrow = strtotime('tomorrow', $now);
$nextWeek = strtotime('+1 week', $now);
$yesterday = strtotime('yesterday', $now);
$lastMonth = strtotime('-1 month', $now);

echo date('Y-m-d', $tomorrow);   // Tomorrow's date
echo date('Y-m-d', $nextWeek);   // Date 7 days from now

// Complex relative dates
$timestamp = strtotime('next Monday');
$timestamp = strtotime('last Friday');
$timestamp = strtotime('+2 days +3 hours');
$timestamp = strtotime('first day of next month');
$timestamp = strtotime('last day of this month');

echo date('Y-m-d', strtotime('next Monday'));`}
              />
            </div>

            {/* DateTime::getTimestamp() */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-sm mr-3">2</span>
                {t('php.dateTimeToTimestamp')}
              </h3>
              <CodeBlock
                id="php-8"
                code={`<?php
// Create DateTime from string
$datetime = new DateTime('2025-01-04 12:00:00');
$timestamp = $datetime->getTimestamp();
echo $timestamp;  // Output: 1735948800

// Different formats
$dt1 = new DateTime('2025-01-04');
$dt2 = new DateTime('January 4, 2025');
$dt3 = new DateTime('01/04/2025 12:00');

echo $dt1->getTimestamp();

// Create from format
$datetime = DateTime::createFromFormat('d/m/Y H:i', '04/01/2025 12:00');
$timestamp = $datetime->getTimestamp();
echo $timestamp;

// With timezone
$datetime = new DateTime('2025-01-04 12:00:00', new DateTimeZone('Asia/Shanghai'));
$timestamp = $datetime->getTimestamp();
echo $timestamp;`}
              />
            </div>
          </div>
        </div>

        {/* Timezone Handling */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('php.timezoneTitle')}</h2>
          <p className="text-gray-600 mb-6">{t('php.timezoneDesc')}</p>

          <div className="space-y-6">
            {/* Set Timezone */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('php.setTimezone')}</h3>
              <CodeBlock
                id="php-9"
                code={`<?php
// Set default timezone for entire script
date_default_timezone_set('Asia/Shanghai');

// Now all date functions use this timezone
echo date('Y-m-d H:i:s');

// Get current timezone
$timezone = date_default_timezone_get();
echo $timezone;  // Output: Asia/Shanghai

// Common timezones
date_default_timezone_set('UTC');
date_default_timezone_set('America/New_York');
date_default_timezone_set('Europe/London');
date_default_timezone_set('Asia/Tokyo');

// List all available timezones
$timezones = DateTimeZone::listIdentifiers();
print_r($timezones);`}
              />
            </div>

            {/* DateTimeZone class */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('php.dateTimeZone')}</h3>
              <CodeBlock
                id="php-10"
                code={`<?php
// Create DateTime with specific timezone
$utc = new DateTimeZone('UTC');
$beijing = new DateTimeZone('Asia/Shanghai');
$newYork = new DateTimeZone('America/New_York');

// Create DateTime in specific timezone
$datetime = new DateTime('now', $beijing);
echo $datetime->format('Y-m-d H:i:s T');
// Output: 2025-01-04 12:00:00 CST

// Convert between timezones
$datetimeBeijing = new DateTime('2025-01-04 12:00:00', $beijing);
$datetimeBeijing->setTimezone($newYork);
echo $datetimeBeijing->format('Y-m-d H:i:s T');
// Output: 2025-01-03 23:00:00 EST

// Get timezone offset
$offset = $beijing->getOffset(new DateTime());
echo "Offset: " . ($offset / 3600) . " hours";

// Compare times in different timezones
$dt1 = new DateTime('2025-01-04 12:00:00', new DateTimeZone('Asia/Shanghai'));
$dt2 = new DateTime('2025-01-04 12:00:00', new DateTimeZone('America/New_York'));

// Both have same timestamp if same UTC time
echo $dt1->getTimestamp();
echo $dt2->getTimestamp();`}
              />
            </div>
          </div>
        </div>

        {/* Common Patterns */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">{t('php.patternsTitle')}</h2>

          <div className="space-y-6">
            {/* Date Arithmetic */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('php.pattern1.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('php.pattern1.desc')}</p>
              <CodeBlock
                id="php-11"
                code={`<?php
// Using strtotime() - Simple and intuitive
$now = time();

$tomorrow = strtotime('+1 day', $now);
$nextWeek = strtotime('+1 week', $now);
$nextMonth = strtotime('+1 month', $now);
$yesterday = strtotime('-1 day', $now);
$lastWeek = strtotime('-1 week', $now);

echo date('Y-m-d', $tomorrow);   // Tomorrow
echo date('Y-m-d', $nextWeek);   // One week from now

// Using DateTime and DateInterval - More precise
$datetime = new DateTime();

// Add time
$datetime->add(new DateInterval('P1D'));    // Add 1 day
$datetime->add(new DateInterval('PT2H'));   // Add 2 hours
$datetime->add(new DateInterval('P1M'));    // Add 1 month
$datetime->add(new DateInterval('P1Y'));    // Add 1 year
$datetime->add(new DateInterval('P1DT12H')); // Add 1 day 12 hours

// Subtract time
$datetime->sub(new DateInterval('P7D'));    // Subtract 7 days

// Using modify() - Most flexible
$datetime = new DateTime();
$datetime->modify('+1 day');
$datetime->modify('+2 weeks');
$datetime->modify('-1 month');
$datetime->modify('next Monday');
$datetime->modify('last Friday');

echo $datetime->format('Y-m-d H:i:s');`}
              />
            </div>

            {/* Parse Relative Dates */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('php.pattern2.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('php.pattern2.desc')}</p>
              <CodeBlock
                id="php-12"
                code={`<?php
// strtotime() is very powerful for parsing relative dates

// Next/Last day of week
$nextMonday = strtotime('next Monday');
$lastFriday = strtotime('last Friday');
$thisSaturday = strtotime('this Saturday');

echo date('Y-m-d', $nextMonday);   // Next Monday's date

// Start/End of month
$firstDayOfMonth = strtotime('first day of this month');
$lastDayOfMonth = strtotime('last day of this month');
$firstDayOfNextMonth = strtotime('first day of next month');

echo date('Y-m-d', $firstDayOfMonth);  // 2025-01-01
echo date('Y-m-d', $lastDayOfMonth);   // 2025-01-31

// Relative with specific day
$nextWeekMonday = strtotime('next week Monday');
$twoWeeksFromNow = strtotime('+2 weeks');
$threeMonthsAgo = strtotime('-3 months');

// Complex expressions
$timestamp = strtotime('last day of December 2025');
$timestamp = strtotime('first Monday of January 2025');
$timestamp = strtotime('+2 days +3 hours +5 minutes');

// Using DateTime
$datetime = new DateTime('first day of next month');
echo $datetime->format('Y-m-d');

$datetime = new DateTime('last Friday of this month');
echo $datetime->format('Y-m-d');`}
              />
            </div>

            {/* Compare Timestamps */}
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">{t('php.pattern3.title')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('php.pattern3.desc')}</p>
              <CodeBlock
                id="php-13"
                code={`<?php
// Compare timestamps directly
$ts1 = strtotime('2025-01-04 12:00:00');
$ts2 = strtotime('2025-01-06 15:30:00');

if ($ts1 < $ts2) {
    echo "ts1 is earlier than ts2";
}

// Calculate difference in seconds
$difference = $ts2 - $ts1;
echo "Difference: $difference seconds";

// Convert to days/hours/minutes
$days = floor($difference / 86400);
$hours = floor(($difference % 86400) / 3600);
$minutes = floor(($difference % 3600) / 60);
$seconds = $difference % 60;

echo "$days days, $hours hours, $minutes minutes, $seconds seconds";

// Using DateTime - More readable
$dt1 = new DateTime('2025-01-04 12:00:00');
$dt2 = new DateTime('2025-01-06 15:30:00');

// Compare
if ($dt1 < $dt2) {
    echo "dt1 is earlier than dt2";
}

// Calculate difference
$interval = $dt1->diff($dt2);

echo $interval->format('%d days, %h hours, %i minutes');
// Output: 2 days, 3 hours, 30 minutes

echo $interval->days;      // Total days
echo $interval->h;         // Hours
echo $interval->i;         // Minutes
echo $interval->s;         // Seconds

// Check if dates are the same day
function isSameDay($ts1, $ts2) {
    return date('Y-m-d', $ts1) === date('Y-m-d', $ts2);
}

echo isSameDay(time(), strtotime('today')) ? 'Same day' : 'Different day';`}
              />
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl shadow-lg p-6 md:p-8 mb-6 md:mb-8 border-2 border-yellow-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            ⚡ {t('common.bestPractices')}
          </h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">time()</code> for simple timestamp operations - it's fast and efficient</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use DateTime class for complex date operations and timezone handling</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Always set timezone with <code className="bg-gray-100 px-1.5 py-0.5 rounded">date_default_timezone_set()</code> at the start of your script</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Store timestamps in UTC in databases, convert to local timezone for display</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Use <code className="bg-gray-100 px-1.5 py-0.5 rounded">strtotime()</code> for parsing flexible date strings</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-600 mr-2">✓</span>
              <span className="text-gray-700">Prefer <code className="bg-gray-100 px-1.5 py-0.5 rounded">DateTimeImmutable</code> over <code className="bg-gray-100 px-1.5 py-0.5 rounded">DateTime</code> to avoid accidental mutations</span>
            </li>
          </ul>
        </div>

        {/* Related Tools */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t('common:relatedTools', { defaultValue: 'Related Tools' })}
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            <a href="/timestamp-converter" className="block p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Unix Timestamp Converter</h3>
              <p className="text-sm text-gray-600">Convert timestamps online</p>
            </a>
            <a href="/python-timestamp" className="block p-4 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">Python Timestamp Guide</h3>
              <p className="text-sm text-gray-600">datetime, time modules</p>
            </a>
            <a href="/javascript-timestamp" className="block p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg border border-yellow-200 transition-colors">
              <h3 className="font-semibold text-gray-800 mb-1">JavaScript Timestamp</h3>
              <p className="text-sm text-gray-600">Date object guide</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
